import { useEffect, useState } from "react";
import { X, Check, Plus } from "lucide-react";

import { useAppStore } from "@/lib/store";
import {
  usePlaylists,
  usePlaylistCourses,
  useAddCourseToPlaylist,
  isCourseInPlaylist,
} from "@/hooks/usePlaylists";

export function SaveToPerformanceModal() {
  const {
    activeModal,
    closeModal,
    openModal,
    selectedAddToPlaylistCourseId,
  } = useAppStore();
  const {
    data: playlists,
    loading: playlistsLoading,
    error: playlistsError,
    refetch: refetchPlaylists,
  } = usePlaylists();
  const { data: playlistCourses, refetch: refetchPlaylistCourses } = usePlaylistCourses();
  const { execute: addCourseToPlaylist, loading: adding } = useAddCourseToPlaylist();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [addedTo, setAddedTo] = useState<string | null>(null);

  const isOpen = activeModal === "save-to-performance";
  const courseId = selectedAddToPlaylistCourseId;

  // Reset state when modal opens
  useEffect(() => {
    if (!isOpen) return;
    setSelectedId(null);
    setAddedTo(null);
    refetchPlaylists();
    refetchPlaylistCourses();
  }, [isOpen, refetchPlaylistCourses, refetchPlaylists]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeModal();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, closeModal]);

  if (!isOpen || !courseId) return null;

  const handleAdd = async () => {
    if (!selectedId) return;
    await addCourseToPlaylist(selectedId, courseId);
    setAddedTo(selectedId);
    refetchPlaylistCourses();
  };

  const handleCreateNew = () => {
    closeModal();
    openModal("create-performance");
  };

  const selectedAlreadyIn = selectedId
    ? isCourseInPlaylist(playlistCourses, courseId, selectedId)
    : false;

  return (
    <>
      <button
        type="button"
        aria-label="Close"
        className="fixed inset-0 z-50 bg-[rgba(29,13,24,0.58)]"
        onClick={closeModal}
      />
      <div className="fixed left-1/2 top-1/2 z-[60] w-[min(30rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 rounded-[1.3rem] bg-white shadow-[0_40px_120px_rgba(31,11,23,0.28)]">
        {/* Header */}
        <div className="flex items-center gap-2 border-b border-[#ede4e9] px-6 py-5">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#e2e4e9] text-[#525866]">
            <Plus className="h-5 w-5" />
          </div>
          <h2 className="m-0 flex-1 font-[family-name:var(--font-display)] text-[1.2rem] font-semibold text-[#321b29]">
            Select or create a new Performance
          </h2>
          <button
            type="button"
            className="inline-flex h-8 w-8 items-center justify-center rounded-full text-[#7f777d] transition-colors hover:bg-[#faf5f8] hover:text-[#321b29]"
            aria-label="Close"
            onClick={closeModal}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Playlist list */}
        <div className="max-h-[50vh] overflow-y-auto px-6 py-4">
          {playlistsLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className="h-12 animate-pulse rounded-[0.8rem] bg-neutral-100"
                />
              ))}
            </div>
          ) : playlistsError ? (
            <div className="space-y-3 text-center">
              <p className="text-[0.96rem] text-[#7f777d]">
                We could not load your performances right now.
              </p>
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-[0.8rem] border border-[#e2e4e9] bg-white px-4 py-2 text-[0.9rem] font-medium text-[#762a5c] transition-colors hover:bg-[#faf5f8]"
                onClick={refetchPlaylists}
              >
                Try again
              </button>
            </div>
          ) : playlists === null ? (
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className="h-12 animate-pulse rounded-[0.8rem] bg-neutral-100"
                />
              ))}
            </div>
          ) : playlists.length === 0 ? (
            <p className="text-center text-[0.96rem] text-[#7f777d]">
              You have no performances yet. Create one below.
            </p>
          ) : (
            <div className="space-y-1">
              {playlists.map((playlist) => {
                const alreadyIn = isCourseInPlaylist(
                  playlistCourses,
                  courseId,
                  playlist.id,
                );
                const justAdded = addedTo === playlist.id;
                const isSelected = selectedId === playlist.id;

                return (
                  <button
                    key={playlist.id}
                    type="button"
                    className={`flex w-full items-center gap-3 rounded-[0.6rem] px-3 py-3 text-left transition-colors ${
                      isSelected
                        ? "bg-[#faf5f8]"
                        : "hover:bg-[#faf5f8]"
                    }`}
                    onClick={() => setSelectedId(playlist.id)}
                  >
                    {/* Radio circle */}
                    <span
                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
                        isSelected
                          ? "border-[#762a5c] bg-[#762a5c]"
                          : "border-[#ccc8cb]"
                      }`}
                    >
                      {isSelected && (
                        <span className="h-2 w-2 rounded-full bg-white" />
                      )}
                    </span>

                    <span className="flex-1 text-[0.96rem] font-medium text-[#271f24]">
                      {playlist.name}
                    </span>

                    {(alreadyIn || justAdded) && (
                      <span className="inline-flex items-center gap-1 text-[0.82rem] text-green-600">
                        <Check className="h-3.5 w-3.5" />
                        {justAdded ? "Added!" : "Already Added"}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer buttons */}
        <div className="grid grid-cols-2 gap-3 border-t border-[#ede4e9] px-6 py-4">
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-[0.8rem] border border-[#ddd9dc] bg-white px-5 py-3 text-[0.93rem] font-semibold leading-none text-[#2a2227] transition-transform duration-200 hover:-translate-y-[3%] active:scale-95"
            onClick={handleCreateNew}
          >
            Create New Performance
          </button>
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-[0.8rem] bg-[#762a5c] px-5 py-3 text-[0.93rem] font-semibold leading-none text-white transition-transform duration-200 hover:-translate-y-[3%] active:scale-95 disabled:opacity-50"
            disabled={!selectedId || adding || selectedAlreadyIn}
            onClick={() => void handleAdd()}
          >
            {adding ? "Adding..." : "Add to Performance"}
          </button>
        </div>
      </div>
    </>
  );
}
